import { zodResolver } from "@hookform/resolvers/zod";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  usePasswordStepUp,
  useSsoStepUp,
  useStepUpOptions,
} from "@/hooks/use-resource-internal-rates";
import { signIn } from "@/lib/auth-client";

/** The query flag the SSO round trip comes back with. */
const RETURN_FLAG = "stepup";

const passwordSchema = z.object({ password: z.string().min(1, "Enter your password.") });
type PasswordValues = z.infer<typeof passwordSchema>;

const PROVIDER_LABEL = { google: "Google", microsoft: "Microsoft" } as const;

interface Proof {
  stepUpId: string;
  expiresAt: string;
}

/**
 * Asks the user to prove it is still them before rendering `children` (product decision
 * 2026-10-14 — Configuration → Internal Rates shows Resource-level cost).
 *
 * The proof lives in this component's state only: leave the page or refresh and it is
 * gone, so every visit asks again. It also lapses when the server's window ends, and the
 * child calls `onExpired` if a request is refused mid-visit. The server enforces all of
 * this independently — this gate only decides what to show.
 */
export function StepUpGate({
  children,
}: {
  children: (stepUpId: string, onExpired: () => void) => ReactNode;
}) {
  const [proof, setProof] = useState<Proof | null>(null);
  const onExpired = useCallback(() => {
    setProof(null);
    toast.info("Your confirmation expired. Confirm it's you again to continue.");
  }, []);

  // Forget the proof when its window closes, so the page never sits open on a stale one.
  useEffect(() => {
    if (proof === null) return;
    const ms = new Date(proof.expiresAt).getTime() - Date.now();
    const timer = setTimeout(() => setProof(null), Math.max(ms, 0));
    return () => clearTimeout(timer);
  }, [proof]);

  if (proof !== null) return <>{children(proof.stepUpId, onExpired)}</>;
  return <StepUpPrompt onVerified={setProof} />;
}

function StepUpPrompt({ onVerified }: { onVerified: (proof: Proof) => void }) {
  const options = useStepUpOptions();
  const password = usePasswordStepUp();
  const sso = useSsoStepUp();
  const [redirecting, setRedirecting] = useState<"google" | "microsoft" | null>(null);
  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: "" },
  });

  // Back from an SSO sign-in: claim the proof for the fresh session, then drop the flag
  // so a refresh asks again instead of re-claiming.
  const claimed = useRef(false);
  const ssoMutate = sso.mutate;
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (claimed.current || !params.has(RETURN_FLAG)) return;
    claimed.current = true;
    params.delete(RETURN_FLAG);
    const query = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    ssoMutate(undefined, { onSuccess: onVerified });
  }, [ssoMutate, onVerified]);

  async function signInAgain(provider: "google" | "microsoft") {
    setRedirecting(provider);
    const here = window.location.pathname;
    const { error } = await signIn.social({
      provider,
      callbackURL: `${here}?${RETURN_FLAG}=1`,
      errorCallbackURL: here,
    });
    if (error) {
      setRedirecting(null);
      toast.error(error.message ?? `Couldn't start ${PROVIDER_LABEL[provider]} sign-in.`);
    }
  }

  return (
    <Card className="mx-auto w-full max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="size-5 text-primary" aria-hidden />
          Confirm it's you
        </CardTitle>
        <CardDescription>
          Internal Rates are each Resource's own cost. Confirm it's you to open this page. You'll be
          asked again each time you come back.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {options.isLoading || sso.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : options.isError ? (
          <ErrorState
            message={options.error.message}
            onRetry={() => {
              options.refetch();
            }}
          />
        ) : options.data === undefined ||
          (!options.data.password && options.data.providers.length === 0) ? (
          <p className="text-sm text-muted-foreground">
            Your sign-in has no password or linked Google or Microsoft sign-in to confirm with. Ask
            an Admin to set a password for you on the Users page.
          </p>
        ) : (
          <>
            {options.data.password && (
              <Form {...form}>
                <form
                  className="flex flex-col gap-3"
                  onSubmit={form.handleSubmit((values) =>
                    password.mutate(values.password, {
                      onSuccess: onVerified,
                      onError: () => form.resetField("password"),
                    }),
                  )}
                >
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="current-password"
                            autoFocus
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" disabled={password.isPending}>
                    {password.isPending ? (
                      <Loader2 className="size-4 animate-spin" aria-hidden />
                    ) : (
                      <KeyRound className="size-4" aria-hidden />
                    )}
                    Confirm
                  </Button>
                </form>
              </Form>
            )}
            {options.data.password && options.data.providers.length > 0 && (
              <p className="text-center text-xs text-muted-foreground">or</p>
            )}
            {options.data.providers.map((provider) => (
              <Button
                key={provider}
                variant="outline"
                disabled={redirecting !== null}
                onClick={() => {
                  void signInAgain(provider);
                }}
              >
                {redirecting === provider
                  ? `Redirecting to ${PROVIDER_LABEL[provider]}…`
                  : `Sign in again with ${PROVIDER_LABEL[provider]}`}
              </Button>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}
